import { RolesApi } from "@/Administration/Api/Roles/RolesApi";
import { RoleHeaderModel } from "@/Administration/Models/Role/RoleHeaderModel";
import { ClickableTextSectionEnum } from "@/Administration/Models/UI/Users/ClickableTextSectionEnum";
import { IUsersTabModel } from "@/Administration/Models/UI/Users/IUsersTabModel";
import { UserEditMode } from "@/Administration/Models/UI/Users/UserEditMode";
import { UsersSubmenuEnum } from "@/Administration/Models/UI/Users/UsersSubmenuEnum";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { ServiceComboDefinitionModel } from "@/Common/Models/Services/ServiceComboDefinitionModel";
import { ServiceDefinitionModel } from "@/Common/Models/Services/ServiceDefinitionModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { cacheStore } from "@/Globals/Stores/CacheStore";
import { ServiceComboSearchSettings } from "../Services/ServiceComboSearchSettings";
import { ClickableTextImportation } from "./ClickableTextImportation";
import { DeactivateMultifactorAuthenticationWarningPopup } from "./DeactivateMultifactorAuthenticationWarningPopup";
import { EmployeeBannerComponent } from "./EmployeeBannerComponent";
import { UserAddNewServiceComboComponent } from "./UserAddNewServiceComboComponent";
import { UserConfigurationInfoComponent } from "./UserConfigurationInfoComponent";
import { UserConfigurationInfoEditComponent } from "./UserConfigurationInfoEditComponent";
import { UserDemographicInfoComponent } from "./UserDemographicInfo";
import { UserDemographicInfoEditComponent } from "./UserDemographicInfoEditComponent";
import { UserDetailsPanelMenuComponent } from "./UserDetailsPanelMenuComponent";
import { UserPracticeInfoComponent } from "./UserPracticeInfoComponent";
import { UserPracticeInfoEditComponent } from "./UserPracticeInfoEditComponent";
import { UserToolsInfoComponent } from "./UserToolsInfoComponent";
import { UserToolsInfoEditComponent } from "./UserToolsInfoEditComponent";

import newServiceStyles from "./UserAddNewServiceComboComponent.module.scss";
import styles from "./UserDetailsPanelComponent.module.scss";

interface BaseEditableUserInfoPanelProps {
    model: IUsersTabModel;
    editedUser?: EmployeeUIModel;
    isValidCallBack: (isValid: boolean) => void;
    mutateParentEmployee: (parentMutation: (emp: EmployeeUIModel) => void) => void;
}
interface UserDetailsPanelComponentProps {
    model: IUsersTabModel;
    serviceDefinitionCache: Immutable.List<ServiceDefinitionModel>;
    serviceComboDefinitionCache: Immutable.List<ServiceComboDefinitionModel>;
}

class UserDetailsPanelComponentState {
    public canNavigate: boolean;
    public canSaveDemographic: boolean;
    public canSaveUserPracticeInfo: boolean;
    public canSaveUserConfigurationInfo: boolean;
    public canSaveUserToolsInfo: boolean;
    public disableSave: boolean;
    public extendedSections: ClickableTextSectionEnum[];
    public shouldDisplayDeactivateMultifactorAuthenticationMessage: boolean;
    public roleDefinitions: RoleHeaderModel[];
}

class UserDetailsPanelComponent extends React.Component<UserDetailsPanelComponentProps, UserDetailsPanelComponentState> {
    constructor(props: UserDetailsPanelComponentProps) {
        super(props);

        this.state = this.buildInitialState(true);
    }

    public componentDidMount(): void {
        $(window).trigger("resize");

        // if this component is remounted and already has a user loaded and is not in edit mode, refresh the employee info
        if (this.props.model.get("SelectedEmployee")?.ContactFacetId != null &&
            this.props.model.get("UserEditMode") != UserEditMode.Edit)
            window.setTimeout(() => {
                var operation = new LocalEvent(LocalOperations.AdministrationUsers.GetEmployeeInfo);
                operation.AddItem("EmployeeId", this.props.model.get("SelectedEmployee").ContactFacetId);
                LocalEventDispatcher.dispatch(operation);
            }, 0);

        if (cacheStore.ClinicSettings.EnableRoleBasedAccessControl)
            this.loadRolePermissionHeaders();
    }

    public componentDidUpdate(prevProps: UserDetailsPanelComponentProps): void {
        if (this.props.model.get("UserEditMode") !== prevProps.model.get("UserEditMode"))
            this.setState(
                this.buildInitialState(),
                () => {
                    if (cacheStore.ClinicSettings.EnableRoleBasedAccessControl)
                        this.loadRolePermissionHeaders();
                }
            );
    }

    public render(): JSX.Element {
        if (this.props.model == null || (this.props.model.get("SelectedEmployee") == null && this.props.model.get("SelectedEmployee") == null))
            return <div className="fxheight admin-users-right-section" />;

        return (
            <div className="fxheight admin-users-right-section">

                <EmployeeBannerComponent
                    model={this.props.model}
                    clinicSettings={cacheStore.ClinicSettings}
                />

                {this.props.model.get("IsOpenAddNewServiceCombo") === true && (
                    <UserAddNewServiceComboComponent
                        serviceComboGridModel={this.props.model.get("UserServiceComboGridModel")}
                        editedUser={this.props.model.get("EditedUser")}
                        userServiceComboGridModel={this.props.model.get("UserServiceComboGridModel")}
                        serviceDefinitionCache={this.props.serviceDefinitionCache}
                        serviceComboDefinitionCache={this.props.serviceComboDefinitionCache}
                        refreshServiceOperation={LocalOperations.AdministrationUsers.RefreshServices}
                        closeOperation={LocalOperations.AdministrationUsers.AddUserServiceComboPopupClose}
                        saveOperation={LocalOperations.AdministrationUsers.SaveNewServicesCombo}
                        closeSearchSettingOperation={LocalOperations.AdministrationUsers.SearchSettings_Close}
                        openSearchSettingOperation={LocalOperations.AdministrationUsers.SearchSettings_Open}
                    />
                )}

                {this.props.model.get("UserServiceComboGridModel").get("SearchSettingsOpen") && (
                    <Popup
                        cssClass="centered-popup"
                        topPosition={106}
                        leftPosition={384}
                        onCloseDelegate={() => sessionStore.dispatch(LocalOperations.AdministrationUsers.SearchSettings_Close)}
                    >
                        <ServiceComboSearchSettings
                            id="User_add_servicecombosettings"
                            model={this.props.model.get("UserServiceComboGridModel")}
                            className={`filter-component small ${newServiceStyles.serviceComboSearchSettings}`}
                            onCancelCallBack={() => sessionStore.dispatch(LocalOperations.AdministrationUsers.SearchSettings_Close)}
                            onOkCallBack={() => this.onApplySearchSettings(false, true)}
                            refreshServiceCallBack={() => this.onApplySearchSettings(false, false)}
                        />
                    </Popup>
                )}

                {this.props.model.get("IsImportClickableTextOpen") === true && (
                    <ClickableTextImportation
                        clickableTextItems={this.props.model.get("EmployeeClickableTextItems")}
                        usersTabModel={this.props.model}
                        tab={this.props.model.get("SelectedTabImportClickableText")}
                        isGroupMode={false}
                    />
                )}

                {this.props.model.get("SelectedEmployee") && (
                    <UserDetailsPanelMenuComponent
                        selectedTab={this.props.model.get("SelectedSubmenuItem")}
                        userType={this.props.model.get("UserType")}
                        editedUser={this.props.model.get("EditedUser")}
                        userMode={this.props.model.get("UserEditMode")}
                        canSaveConfiguration={this.state?.canSaveUserConfigurationInfo ?? false}
                        canSaveDemographic={this.state?.canSaveDemographic ?? false}
                        canSaveUserPracticeInfo={this.state?.canSaveUserPracticeInfo ?? false}
                    />
                )}

                {this.state.shouldDisplayDeactivateMultifactorAuthenticationMessage && (
                    <DeactivateMultifactorAuthenticationWarningPopup
                        onCancel={() => this.mutateState(s => s.shouldDisplayDeactivateMultifactorAuthenticationMessage = false)}
                        onConfirm={() => {
                            this.mutateEmployee(emp => emp.Model.AuthenticatorEnabled = false);
                            this.mutateState(
                                s => {
                                    s.shouldDisplayDeactivateMultifactorAuthenticationMessage = false;
                                    s.disableSave = true;
                                },
                                () => sessionStore.dispatch(LocalOperations.AdministrationUsers.SaveUser)
                            );
                        }}
                    />
                )}
                <div
                    data-scrolling
                    data-hvar={60}
                    className={`scrollable ${cacheStore.ClinicSettings.EnableUserSettingsUiImprovements
                        ? styles.userDetailsPanel
                        : ""}`
                    }
                >
                    <div
                        className={`${cacheStore.ClinicSettings.EnableUserSettingsUiImprovements
                            ? styles.container
                            : ""}`
                        }
                    >
                        {this.switchTabComponents(this.props.model.get("SelectedSubmenuItem"), this.props.model.get("UserEditMode"))}
                    </div>
                </div>

                <div className="meta-footer">

                    {this.props.model.get("UserEditMode") == UserEditMode.View && this.props.model.get("SelectedEmployee") != null && (
                        <ButtonControl
                            onClick={() => sessionStore.dispatch(LocalOperations.AdministrationUsers.EditUser)}
                            label={cacheStore.GetString("EDITLabel")}
                            className="btn btn-primary pull-right"
                            dataCy="userProfile-editButton"
                        />
                    )}

                    {this.props.model.get("UserEditMode") == UserEditMode.Edit &&
                        this.renderEditModeButtons()
                    }

                    {this.props.model.get("UserEditMode") == UserEditMode.Create &&
                        this.renderCreateModeButtons()
                    }
                </div>
            </div>
        );
    }

    private buildInitialState(initExtendedSection?: boolean): UserDetailsPanelComponentState {
        const user = this.props.model.get("EditedUser");
        const initialState = new UserDetailsPanelComponentState();

        initialState.canSaveUserToolsInfo = true; // true by default because there is nothing mandatory on that page

        if (this.props.model.get("UserEditMode") == UserEditMode.Create) {
            initialState.canSaveDemographic = false;
            initialState.canSaveUserConfigurationInfo = false;
            initialState.canSaveUserPracticeInfo = false;
        }
        else if (this.props.model.get("UserEditMode") == UserEditMode.Edit) {
            initialState.canSaveDemographic =
                    !IsNullOrWhiteSpace(user.Model.FirstName)
                    && !IsNullOrWhiteSpace(user.Model.LastName)
                    && user.isPhoneValid()
                    && user.isEmailValid();

            initialState.canSaveUserPracticeInfo = user.Type === EmployeeType.Practician
                ? !!user.getIssuingOrgRule()
                : true; // true since the panel is not available for clerical accounts

            initialState.canSaveUserConfigurationInfo = this.getCanSaveUserConfiguration(user.Model, []);
        }

        initialState.disableSave = false;
        initialState.shouldDisplayDeactivateMultifactorAuthenticationMessage = false;
        if (initExtendedSection)
            initialState.extendedSections = [ClickableTextSectionEnum.ClinicalNote];

        return initialState;
    }

    private renderCreateModeButtons(): JSX.Element {
        const isValid = this.canSave();
        const canNavigate = this.canNavigate();
        const disabled = this.state.disableSave;

        return (
            <div>
                <ButtonControl
                    disabled={!isValid || disabled}
                    label={cacheStore.GetString("SaveLabel")}
                    onClick={() => this.disableSaveButtonAndTriggerSave()}
                    className="btn btn-primary pull-right"
                    dataCy="userProfile-saveButton"
                />
                {this.props.model.get("UserType") !== EmployeeType.Api && (
                    <div>
                        {this.props.model.get("SelectedSubmenuItem") != UsersSubmenuEnum.Tools && (
                            <ButtonControl
                                disabled={!canNavigate}
                                onClick={() => sessionStore.dispatch(LocalOperations.AdministrationUsers.EditUserNextSection)}
                                label={cacheStore.GetString("NextLabel")}
                                className="btn btn-default pull-right push-right-5"
                                dataCy="userProfile-nextSectionButton"
                            />
                        )}

                        {this.props.model.get("SelectedSubmenuItem") != UsersSubmenuEnum.Demography && (
                            <ButtonControl
                                disabled={!canNavigate}
                                onClick={() => sessionStore.dispatch(LocalOperations.AdministrationUsers.EditUserPreviousSection)}
                                label={cacheStore.GetString("PreviousLabel")}
                                className="btn btn-default pull-right push-right-5"
                                dataCy="userProfile-previousSectionButton"
                            />
                        )}
                    </div>
                )}
                <ButtonControl
                    label={cacheStore.GetString("CancelLabel")}
                    onClick={() => sessionStore.dispatch(LocalOperations.AdministrationUsers.CancelEditUser)}
                    className="btn btn-default pull-right push-right-5"
                    dataCy="userProfile-cancelButton"
                />
            </div>
        );
    }

    private renderEditModeButtons(): JSX.Element {
        const isValid = this.canSave();
        const canNavigate = this.canNavigate();
        const disabled = this.state.disableSave;

        return (
            <div>
                <ButtonControl
                    disabled={!isValid || disabled}
                    label={cacheStore.GetString("SaveLabel")}
                    className="btn btn-primary pull-right"
                    onClick={() => this.handleSaveEditedUser()}
                    dataCy="userProfile-saveButton"
                />
                {this.props.model.get("UserType") !== EmployeeType.Api && (
                    <div>
                        {this.props.model.get("SelectedSubmenuItem") != UsersSubmenuEnum.Tools && (
                            <ButtonControl
                                disabled={!canNavigate}
                                onClick={() => sessionStore.dispatch(LocalOperations.AdministrationUsers.EditUserNextSection)}
                                label={cacheStore.GetString("NextLabel")}
                                className="btn btn-default pull-right push-right-5"
                                dataCy="userProfile-nextSectionButton"
                            />
                        )}

                        {this.props.model.get("SelectedSubmenuItem") != UsersSubmenuEnum.Demography && (
                            <ButtonControl
                                disabled={!canNavigate}
                                onClick={() => sessionStore.dispatch(LocalOperations.AdministrationUsers.EditUserPreviousSection)}
                                label={cacheStore.GetString("PreviousLabel")}
                                className="btn btn-default pull-right push-right-5"
                                dataCy="userProfile-previousSectionButton"
                            />
                        )}
                    </div>
                )}
                <ButtonControl
                    onClick={() => sessionStore.dispatch(LocalOperations.AdministrationUsers.CancelEditUser)}
                    label={cacheStore.GetString("CancelLabel")}
                    className="btn btn-default pull-right push-right-5"
                    dataCy="userProfile-cancelButton"
                />
            </div>
        );
    }

    private switchTabComponents(current: UsersSubmenuEnum, editMode: UserEditMode): JSX.Element {
        switch (current) {
            case UsersSubmenuEnum.Demography:
                if (editMode == UserEditMode.View)
                    return (
                        <UserDemographicInfoComponent
                            inProgress={this.props.model.get("ResendEmailVerificationCodeInProgress")}
                            employeeModel={this.props.model.get("SelectedEmployee")}
                        />
                    );
                else
                    return (
                        <UserDemographicInfoEditComponent
                            model={this.props.model}
                            editedUser={this.props.model.get("EditedUser")}
                            mutateParentEmployee={mutation =>
                                this.mutateEmployee(emp => mutation(emp))
                            }
                            isValidCallBack={isValid =>
                                this.mutateState(s => s.canSaveDemographic = isValid)
                            }
                        />
                    );

            case UsersSubmenuEnum.Practice:
                if (editMode == UserEditMode.View)
                    return (
                        <UserPracticeInfoComponent
                            employeeModel={this.props.model.get("SelectedEmployee")}
                        />
                    );
                else
                    return (
                        <UserPracticeInfoEditComponent
                            model={this.props.model}
                            editedUser={this.props.model.get("EditedUser")}
                            mutateParentEmployee={mutation =>
                                this.mutateEmployee(emp => mutation(emp))
                            }
                            isValidCallBack={isValid =>
                                this.mutateState(s => s.canSaveUserPracticeInfo = isValid)
                            }
                            clinicSettings={cacheStore.ClinicSettings}
                        />
                    );

            case UsersSubmenuEnum.Configuration:
                if (editMode == UserEditMode.View)
                    return (
                        <UserConfigurationInfoComponent
                            employeeModel={this.props.model.get("SelectedEmployee")}
                        />
                    );
                else
                    return (
                        <UserConfigurationInfoEditComponent
                            model={this.props.model}
                            editedUser={this.props.model.get("EditedUser")}
                            mutateParentEmployee={mutation =>
                                this.mutateEmployee(emp => mutation(emp))
                            }
                            isValidCallBack={isValid =>
                                this.mutateState(s => s.canSaveUserConfigurationInfo = isValid)
                            }
                        />
                    );

            case UsersSubmenuEnum.Tools:
                if (editMode == UserEditMode.View)
                    return (
                        <UserToolsInfoComponent
                            extendedSections={this.state.extendedSections}
                            onExtendedSectionsChange={sections => this.mutateState(s => s.extendedSections = sections)}
                            employeeModel={this.props.model.get("SelectedEmployee")}
                            languageOptionForClickableText={this.props.model.get("LanguageOptionClickableText")}
                        />
                    );
                else
                    return (
                        <UserToolsInfoEditComponent
                            model={this.props.model}
                            extendedSections={this.state.extendedSections}
                            onExtendedSectionsChange={sections => this.mutateState(s => s.extendedSections = sections)}
                            editedUser={this.props.model.get("EditedUser")}
                            mutateParentEmployee={mutation =>
                                this.mutateEmployee(emp => mutation(emp))
                            }
                            isValidCallBack={isValid =>
                                this.mutateState(s => s.canSaveUserToolsInfo = isValid)
                            }
                        />
                    );

            default:
                return <div />;
        }
    }

    private mutateState(mutation: (s: UserDetailsPanelComponentState) => void, then: () => void = null): void {
        var s = new UserDetailsPanelComponentState();
        mutation(s);
        this.setState(s, then);
    }

    private mutateEmployee(mutation: (e: EmployeeUIModel) => void): void {
        let emp = this.props.model.get("EditedUser").clone();
        mutation(emp);
        sessionStore.dispatch(
            LocalOperations.AdministrationUsers.MutateEmployeeUIModel,
            { EditedUser: emp }
        );
    }

    private canCreate(): boolean {
        const editType: EmployeeType = this.props.model.get("UserType");
        if (editType === EmployeeType.Admin || editType === EmployeeType.Practician)
            return this.state.canSaveDemographic &&
                this.state.canSaveUserPracticeInfo &&
                this.state.canSaveUserConfigurationInfo &&
                this.state.canSaveUserToolsInfo;
        else
            return this.state.canSaveDemographic &&
                this.state.canSaveUserConfigurationInfo &&
                this.state.canSaveUserToolsInfo;
    }

    private canNavigate(): boolean {
        const editMode: UserEditMode = this.props.model.get("UserEditMode");
        const currentTab: UsersSubmenuEnum = this.props.model.get("SelectedSubmenuItem");

        if (editMode == UserEditMode.Create || editMode == UserEditMode.Edit) {
            if (currentTab == 0)
                return this.state.canSaveDemographic;
            else if (currentTab == 2)
                return this.state.canSaveUserPracticeInfo;
            else if (currentTab == 1)
                return this.state.canSaveUserConfigurationInfo;
            else if (currentTab == 3)
                return this.state.canSaveUserToolsInfo;
        }

        return true;
    }

    private canSave(): boolean {
        const editMode: UserEditMode = this.props.model.get("UserEditMode");

        if (editMode == UserEditMode.Create)
            return this.canCreate();
        else
            return this.state.canSaveDemographic &&
                this.state.canSaveUserPracticeInfo &&
                this.state.canSaveUserConfigurationInfo &&
                this.state.canSaveUserToolsInfo;
    }

    private disableSaveButtonAndTriggerSave(): void {
        this.mutateState(
            s => s.disableSave = true,
            () => sessionStore.dispatch(LocalOperations.AdministrationUsers.SaveUser, { onError: () => {
                this.mutateState(s => s.disableSave = false);
            } })
        );
    }

    private onApplySearchSettings(reload: boolean = false, closeSettings: boolean = false): void {
        sessionStore.dispatch(
            LocalOperations.AdministrationUsers.RefreshServices,
            {
                model: this.props.model.get("UserServiceComboGridModel"),
                reload: reload,
                closeSettings: closeSettings
            }
        );
    }

    private handleSaveEditedUser(): void {
        const editedUser = this.props.model.get("EditedUser");
        const selectedUser = this.props.model.get("SelectedEmployee");

        if (selectedUser.Model.AuthenticatorEnabled && editedUser.Model.Emails[0]?.Address !== selectedUser.Model.Emails[0]?.Address) {
            this.mutateState(s => s.shouldDisplayDeactivateMultifactorAuthenticationMessage = true);
        }
        else {
            this.disableSaveButtonAndTriggerSave();
        }
    }

    private getCanSaveUserConfiguration(employee: Employee, rolePermissions: string[]): boolean {
        const isValid = Employee.isStartDateValid(employee) &&
            Employee.isEndDateValid(employee) &&
            Employee.isTimeoutDelayValid(employee) &&
            Employee.isSitesValid(employee);

        const employeePermissionsAreValid = cacheStore.ClinicSettings.EnableRoleBasedAccessControl ?
            Employee.isPermissionValid(employee) || rolePermissions?.any() :
            Employee.isPermissionValid(employee);

        return isValid && employeePermissionsAreValid;
    }

    private async loadRolePermissionHeaders(): Promise<void> {
        const editedUser = this.props.model.get("EditedUser")?.Model;
        if (!editedUser)
            return;

        const roleDefinitions = await RolesApi.GetCachedRoleHeadersAsync();
        const rolePermissions = this.getResolvedRolePermissions(editedUser.Roles, roleDefinitions.RoleHeaders);
        this.mutateState(s => {
            s.roleDefinitions = roleDefinitions.RoleHeaders;
            s.canSaveUserConfigurationInfo = this.getCanSaveUserConfiguration(editedUser, rolePermissions);
        });
    }

    private getResolvedRolePermissions(selectedRoles: string[], roleDefinitions: RoleHeaderModel[]): string[] {
        if (!roleDefinitions || !selectedRoles)
            return [];

        const allPermissions = selectedRoles.map(roleId => {
            const role = roleDefinitions
                .firstOrDefault(definition => definition.Id === roleId);

            if (role == null) return [];
            return role.PermissionIds;
        });

        return [].concat(...allPermissions).distinct();
    }
}

export { BaseEditableUserInfoPanelProps, UserDetailsPanelComponent };
